import { initFirebase } from '../firebase'
import { collection, getDocs, query, orderBy } from 'firebase/firestore'

const { db } = initFirebase()

const getPlanType = (vendorProductId) => {
  if (!vendorProductId) return 'Unknown'
  const productId = vendorProductId.toLowerCase()
  if (productId.includes('weekly')) return 'Weekly'
  if (productId.includes('monthly')) return 'Monthly'
  if (productId.includes('yearly') || productId.includes('annual')) return 'Yearly'
  return 'Unknown'
}

export class FirebaseAdaptyService {
  async getAllAdaptyWebhooks() {
    const ref = collection(db, 'adapty')
    const q = query(ref, orderBy('receivedAt', 'desc'))
    const snap = await getDocs(q)
    return snap.docs.map(d => ({ id: d.id, ...d.data() }))
  }

  async getCancellationRateData(daysAgo = 7, customStartDate = null, customEndDate = null, groupBy = 'build') {
    try {
      const ref = collection(db, 'adapty')
      
      let startDate, endDate
      
      if (customStartDate && customEndDate) {
        // Parse as UTC to avoid timezone issues
        startDate = new Date(customStartDate + 'T00:00:00.000Z')
        endDate = new Date(customEndDate + 'T23:59:59.999Z')
      } else {
        endDate = new Date()
        endDate.setUTCHours(23, 59, 59, 999)
        startDate = new Date()
        startDate.setUTCDate(startDate.getUTCDate() - daysAgo)
        startDate.setUTCHours(0, 0, 0, 0)
      }
      
      console.log('Date range:', {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        customStartDate,
        customEndDate,
        daysAgo
      })

      const snap = await getDocs(query(ref, orderBy('receivedAt', 'desc')))
      const allData = snap.docs.map(d => ({ id: d.id, ...d.data() }))

      const startedByDate = {}
      const startedProfileIds = new Set()
      const subscriptionsByDate = []

      // First pass: Find all subscriptions started in the date range
      allData.forEach(item => {
        const eventDate = item.event_datetime 
          ? new Date(item.event_datetime) 
          : (item.receivedAt?.seconds ? new Date(item.receivedAt.seconds * 1000) : null)

        if (!eventDate || eventDate < startDate || eventDate > endDate) return
        
        // Skip Sandbox environment
        const environment = item.event_properties?.environment
        if (environment === 'Sandbox') return

        const dateKey = eventDate.toISOString().split('T')[0]

        if (item.event_type === 'subscription_started') {
          const profileId = item.profile_id || item.event_properties?.profile_id
          const customerId = item.customer_user_id
          if (!profileId) return
          
          // Log subscription info
          subscriptionsByDate.push({
            date: dateKey,
            profileId: profileId,
            customerId: customerId
          })
          
          if (!startedByDate[dateKey]) {
            startedByDate[dateKey] = {
              date: dateKey,
              started: [],
              count: 0
            }
          }
          startedByDate[dateKey].started.push(profileId)
          startedByDate[dateKey].count++
          startedProfileIds.add(profileId)
        }
      })
      
      // Second pass: Find cancellations for these subscriptions across ALL time
      const cancelledProfileIds = new Set()
      
      allData.forEach(item => {
        // Skip Sandbox environment
        const environment = item.event_properties?.environment
        if (environment === 'Sandbox') return
        
        if (item.event_type === 'subscription_renewal_cancelled') {
          const profileId = item.profile_id || item.event_properties?.profile_id
          if (!profileId) return
          
          // Only count if this user started a subscription in our date range
          if (startedProfileIds.has(profileId)) {
            cancelledProfileIds.add(profileId)
          }
        }
      })
      
      // Filter out cancelled subscriptions
      const notCancelledSubscriptions = subscriptionsByDate.filter(sub => 
        !cancelledProfileIds.has(sub.profileId)
      )
      
      console.log('Subscriptions NOT cancelled:', notCancelledSubscriptions)
      console.log('Total started:', subscriptionsByDate.length, '| Not cancelled:', notCancelledSubscriptions.length, '| Cancelled:', cancelledProfileIds.size)

      const startedByDateAndBuild = {}
      const buildNumberCounts = {}
      const startedProfileIdsByBuild = new Map() // Map of profileId -> groupKey
      
      // Third pass: Find subscriptions started in date range, grouped by build/duration
      allData.forEach(item => {
        const eventDate = item.event_datetime 
          ? new Date(item.event_datetime) 
          : (item.receivedAt?.seconds ? new Date(item.receivedAt.seconds * 1000) : null)

        if (!eventDate || eventDate < startDate || eventDate > endDate) return
        
        // Skip Sandbox environment
        const environment = item.event_properties?.environment
        if (environment === 'Sandbox') return

        const dateKey = eventDate.toISOString().split('T')[0]
        
        // Group by build number or plan duration
        let groupKey
        if (groupBy === 'duration') {
          const vendorProductId = item.event_properties?.vendor_product_id || ''
          groupKey = getPlanType(vendorProductId)
        } else {
          groupKey = item.user_attributes?.build_number || 'Unknown'
        }

        if (item.event_type === 'subscription_started') {
          const profileId = item.profile_id || item.event_properties?.profile_id
          if (!profileId) return
          
          if (!buildNumberCounts[groupKey]) {
            buildNumberCounts[groupKey] = 0
          }
          buildNumberCounts[groupKey]++

          if (!startedByDateAndBuild[dateKey]) {
            startedByDateAndBuild[dateKey] = {}
          }
          if (!startedByDateAndBuild[dateKey][groupKey]) {
            startedByDateAndBuild[dateKey][groupKey] = {
              started: [],
              count: 0
            }
          }
          startedByDateAndBuild[dateKey][groupKey].started.push(profileId)
          startedByDateAndBuild[dateKey][groupKey].count++
          
          // Store profile-build mapping for later cancellation check
          startedProfileIdsByBuild.set(profileId, groupKey)
        }
      })
      
      // Note: cancelledProfileIds already contains ALL cancellations across time
      // for profiles who started in our date range (from second pass above)

      let sortedBuildNumbers
      if (groupBy === 'duration') {
        // For duration: Weekly, Monthly, Yearly, Unknown
        const order = { 'Weekly': 1, 'Monthly': 2, 'Yearly': 3, 'Unknown': 4 }
        sortedBuildNumbers = Object.keys(buildNumberCounts)
          .sort((a, b) => (order[a] || 999) - (order[b] || 999))
      } else {
        // For build: sort by number descending, Unknown last
        sortedBuildNumbers = Object.keys(buildNumberCounts)
          .sort((a, b) => {
            if (a === 'Unknown') return 1
            if (b === 'Unknown') return -1
            return parseInt(b) - parseInt(a)
          })
          .slice(0, 11) // 10 builds + Unknown
      }

      // Fill in missing dates with zero values
      const allDates = []
      const currentDate = new Date(startDate)
      while (currentDate <= endDate) {
        allDates.push(currentDate.toISOString().split('T')[0])
        currentDate.setDate(currentDate.getDate() + 1)
      }
      
      console.log('Date range includes:', allDates.length, 'days')
      console.log('Found dates with subscription_started:', Object.keys(startedByDate).length, 'days')

      const chartData = []
      allDates.forEach(dateKey => {
        const dayData = startedByDate[dateKey]
        
        // If no data for this date, create empty data point
        if (!dayData) {
          const formattedDate = new Date(dateKey).toLocaleDateString('tr-TR', { 
            month: 'short', 
            day: 'numeric' 
          })
          
          const dataPoint = {
            date: formattedDate,
            value: 0,
            started: 0,
            cancelled: 0,
            builds: {}
          }
          
          // Add zero values for all build numbers
          sortedBuildNumbers.forEach(buildNum => {
            dataPoint.builds[buildNum] = {
              rate: 0,
              started: 0,
              cancelled: 0
            }
          })
          
          chartData.push(dataPoint)
          return
        }
        
        const cancelledCount = dayData.started.filter(profileId => 
          cancelledProfileIds.has(profileId)
        ).length

        const cancellationRate = dayData.count > 0 
          ? ((cancelledCount / dayData.count) * 100).toFixed(1)
          : 0

        const formattedDate = new Date(dateKey).toLocaleDateString('tr-TR', { 
          month: 'short', 
          day: 'numeric' 
        })

        const dataPoint = {
          date: formattedDate,
          value: parseFloat(cancellationRate),
          started: dayData.count,
          cancelled: cancelledCount,
          builds: {}
        }

        sortedBuildNumbers.forEach(buildNum => {
          const buildData = startedByDateAndBuild[dateKey]?.[buildNum]
          if (buildData) {
            const buildCancelledCount = buildData.started.filter(profileId => 
              cancelledProfileIds.has(profileId)
            ).length
            const buildRate = buildData.count > 0 
              ? ((buildCancelledCount / buildData.count) * 100).toFixed(1)
              : 0
            dataPoint.builds[buildNum] = {
              rate: parseFloat(buildRate),
              started: buildData.count,
              cancelled: buildCancelledCount
            }
          } else {
            dataPoint.builds[buildNum] = {
              rate: null,
              started: 0,
              cancelled: 0
            }
          }
        })

        chartData.push(dataPoint)
      })

      return { chartData, buildNumbers: sortedBuildNumbers }
    } catch (error) {
      console.error('Error fetching cancellation rate data:', error)
      return []
    }
  }
}
